import client from 'api/graphQL/config'
import { gql, TypedDocumentNode } from '@apollo/client'
import { LegionJobFilterInput, Query } from 'graphql-api'
import { GraphQLPaginationType, JobOrderType } from 'commonTypes'

const fragments = {
  jobFields: gql`
    fragment jobFields on LegionJob {
      jobId
      raveUrl
      hostname
      databaseId
      status
      createdTime
      lastProcessTime
      jobType
      grafanaTraceLink
      sumoLogicTraceLink
      tasks {
        id
        status
        ... on DataVerificationHashTask {
          createJsonFile
        }
      }
    }
  `,
  jobsData: gql`
    fragment jobsData on JobsConnection {
      totalCount
      pageInfo {
        startCursor
        endCursor
      }
      edges {
        node {
          ...jobFields
        }
        cursor
      }
    }
  `,
  jobDetailsFields: gql`
    fragment metadataFields on Metadata {
      _mdsol_lineage {
        key
        value
      }
      _mdsol_timeliness {
        key
        value
      }
      _mdsol_updated_at
      messageId
      raveDatabaseVersion
      raveUrl
      studyGroupUuid
      transactionEndTime
      transactionId
      eventType
      roleName
      roleUuid
      targetObjectType
      targetObjectUuid
      traceparent
      userLogin
      userUuid
      messageTime
    }
    fragment jobDetailFields on LegionJob {
      requestId
      jobId
      raveUrl
      hostname
      databaseId
      status
      attemptCount
      createdTime
      lastProcessTime
      originationTime
      enqueueTime
      traceHeaders {
        key
        value
      }
      partitionGroup
      jobType
      grafanaTraceLink
      sumoLogicTraceLink
      priority
      tasks {
        id
        status
        attemptCount
        lastProcessedOn
        insertedOn
        partitionKey
        ... on BulkOperationTask {
          bulkOperationQueueId
          subjectId
          objectTypeId
          userId
          roleId
          studyId
          objectIds
          operation
          reviewGroupId
          results {
            objectId
            result
            statusMask
            dataPointCount
          }
        }
        ... on CvRefreshPopReportingRecordsTask {
          projectId
          formOid
          isIncrementalRefresh
          timeToRefresh1Record
          dataPageIds
          taskNumber
        }
        ... on StatusRollupTask {
          taskId
          parentTaskId
          studyId
          objectType
          roleId
          operationType
          refreshClinicalView
        }
        ... on TaskSummaryTask {
          subjectUuid
          taskOperation: operation
          dataPageIds
        }
        ... on TaskSummaryValidationTask {
          subjectUuid
          valid
          taskValidationItems {
            roleId
            parentCount {
              answeredQuery
              answeredQueryReadonly
              answeredQueryCount
              answeredQueryReadonlyCount
              cancelQuery
              cancelQueryCount
              coder
              dataLock
              dataLockEnqueued
              entryLock
              entryLockEnqueued
              nonConformant
              openQuery
              openQueryReadonly
              openQueryCount
              openQueryReadonlyCount
              overdue
              review
              reviewed
              reviewReadonly
              reviewEnqueued
              reviewRequired
              signature
              signatureEnqueued
              sticky
              stickyReadonly
              submitted
              partiallySubmitted
              notSubmitted
              signed
              signatureNotCurrent
              signatureRequired
              complete
              incomplete
              notLocked
              notFrozen
              locked
              frozen
              notEmpty
              total
              inactive
            }
            childrenCount {
              answeredQuery
              answeredQueryReadonly
              answeredQueryCount
              answeredQueryReadonlyCount
              cancelQuery
              cancelQueryCount
              coder
              dataLock
              dataLockEnqueued
              entryLock
              entryLockEnqueued
              nonConformant
              openQuery
              openQueryReadonly
              openQueryCount
              openQueryReadonlyCount
              overdue
              review
              reviewed
              reviewReadonly
              reviewEnqueued
              reviewRequired
              signature
              signatureEnqueued
              sticky
              stickyReadonly
              submitted
              partiallySubmitted
              notSubmitted
              signed
              signatureNotCurrent
              signatureRequired
              complete
              incomplete
              notLocked
              notFrozen
              locked
              frozen
              notEmpty
              total
              inactive
            }
          }
        }
        ... on TaskSummaryRecalculationTask {
          subjectUuid
        }
        ... on NotificationCenterTask {
          notificationQueueId
        }
        ... on TsdvRetrospectiveLegionTask {
          taskId
          subjectId
        }
        ... on TsdvComputeSubjectsReadyToAllocateLegionTask {
          taskId
          subjectId
        }
        ... on ScriptUtilityLegionTask{
          userId
          projectId
          scriptUtilityRunId
        }
        ... on EventMessagingAuditDataLoadTask {
          fromId
          toId
        }
        ... on JobRequesterTask {
          category
          requestedStudyUuid: studyUuid
          objectUuid: objectId
          dataLoadTopicUuid: kafkaTopicUuid
          changesAfterDate
        }
        ... on EventMessagingSortTask {
          startLsn
          endLsn
        }
        ... on EventMessagingSendTask {
          message {
            headers {
              key
              value
            }
            body {
              ... on EdcMessageBody {
                subjects {
                  avroSize
                  subjectId
                  requiresSignature
                  numberInStudyEnvironment
                  numberInStudyEnvironmentSite
                  isUserDeactivated
                  isVersionChanged
                  isAddEventLocked
                  isInitialInvestigatorExternal
                  isCurrentInvestigatorExternal
                  isSentToBalanceUsingGuid
                  subjectUuid
                  subjectName
                  studyEnvironmentSiteUuid
                  studyEnvironmentUuid
                  createdByUserUuid
                  crfVersionId
                  crfVersionUuid
                  crfVersionExternalUuid
                  subjectStatusUuid
                  currentInvestigatorUuid
                  initialInvestigatorUuid
                  reasonDescriptionUuid
                  operationType
                  subjectActive
                  created
                  updated
                  subjectDate
                  secondaryName
                  maxDate
                  deleted
                  isUnavailable
                  reasonDescription
                  operationTypeNumeric
                }
                dataPointsUntranslated {
                  avroSize
                  untranslatedData
                  locale
                  dataPointUntranslatedUuid
                  dataPointUuid
                  dataPointId
                  localeUuid
                  operationType
                  operationTypeNumeric
                }
                sharedSubjects {
                  avroSize
                  created
                  updated
                  deleted
                  sharedSubjectUuid
                  studySiteUuid
                  subjectUuid
                  subjectId
                  operationType
                  operationTypeNumeric
                }
                subjectStatusHistories {
                  avroSize
                  dateChanged
                  subjectStatusHistoryUuid
                  subjectUuid
                  subjectId
                  subjectStatusUuid
                  operationType
                  operationTypeNumeric
                }
                signatures {
                  avroSize
                  signatureId
                  userUuid
                  roleUuid
                  operationType
                  objectTypeId
                  created
                  updated
                  fullName
                  signaturePrompt
                  isActive
                  signatureHash
                  signatureUuid
                  objectUuid
                  operationTypeNumeric
                }
                subjectMatrices {
                  avroSize
                  subjectMatrixId
                  operationType
                  active
                  created
                  updated
                  objectTypeId
                  matrixAction
                  subjectMatrixUuid
                  objectUuid
                  matrixVersionUuid
                  matrixExternalUuid
                  operationTypeNumeric
                }
                instances {
                  avroSize
                  instanceId
                  updated
                  instanceDate
                  deleted
                  requiresSignature
                  instanceRepeatNumber
                  isUserDeactivated
                  balanceVisitIdentifier
                  instanceUuid
                  folderOid
                  folderVersionUuid
                  folderNameUuid
                  folderName
                  instanceName
                  folderExternalUuid
                  subjectUuid
                  parentInstanceUuid
                  subjectMatrixUuid
                  reasonDescriptionUuid
                  objectTagUuids
                  objectTagOids
                  operationType
                  access
                  startWin
                  target
                  endWin
                  overdue
                  close
                  instanceActive
                  created
                  reasonDescription
                  operationTypeNumeric
                }
                dataPages {
                  avroSize
                  dataPageId
                  isUserDeactivated
                  formOid
                  dataPageUuid
                  instanceUuid
                  subjectUuid
                  formVersionUuid
                  formExternalUuid
                  subjectMatrixUuid
                  labUuid
                  reasonDescriptionUuid
                  dataPageName
                  objectTagUuids
                  objectTagOids
                  operationType
                  isPrimaryDataPage
                  dataPageActive
                  pageRepeatNumber
                  created
                  updated
                  dataPageDate
                  requiresSignature
                  deleted
                  reasonDescription
                  operationTypeNumeric
                }
                records {
                  avroSize
                  recordId
                  recordPosition
                  recordDate
                  deleted
                  isUserDeactivated
                  recordUuid
                  dataPageUuid
                  subjectMatrixUuid
                  subjectUuid
                  operationType
                  recordActive
                  created
                  updated
                  entryTime
                  verifyTime
                  primaryReviewTime
                  secondaryReviewTime
                  tertiaryReviewTime
                  lockTime
                  operationTypeNumeric
                }
                dataPoints {
                  avroSize
                  dataPointId
                  reqVerification
                  isVerified
                  reqTranslation
                  reqCoding
                  isUsingCoder
                  wasSigned
                  isSignatureCurrent
                  signatureLevel
                  isFrozen
                  isLocked
                  deleted
                  data
                  rangeStatus
                  rangeUpdated
                  isHidden
                  lastEnteredDate
                  isUserDeactivated
                  altCodedValue
                  deletedOrdinal
                  reqCoderCoding
                  unVerifiedByInactivation
                  unReviewedByInactivation
                  dataActive
                  fieldOid
                  fieldExternalUuid
                  fieldVersionUuid
                  fieldControlType
                  dataPointUuid
                  analyteRangeUuid
                  alertRangeUuid
                  referenceRangeUuid
                  unitDictionaryEntryVersionUuid
                  unitDictionaryEntryExternalUuid
                  unitDictionaryEntryUnitName
                  unitDictionaryEntryUnitNameUuid
                  unitDictionaryStandardUnitName
                  unitDictionaryStandardUnitNameUuid
                  created
                  dataDictionaryEntryVersionUuid
                  dataDictionaryEntryExternalUuid
                  enteredLabUnitUuid
                  labUnitUuid
                  variableVersionUuid
                  variableDataFormat
                  entryLocaleUuid
                  changeCodeUuid
                  objectTagUuids
                  objectTagOids
                  tagNames
                  updated
                  conceptUri
                  attributeUri
                  operationType
                  changeCount
                  lockTime
                  isVisible
                  isTouched
                  isNonConformant
                  standardValue
                  recordUuid
                  operationTypeNumeric
                  coding {
                    coderDecisionUuid
                    updated
                    codingDictionaryVersion
                    values {
                      coderValueUuid
                      value
                      term
                      codingColumnName
                    }
                  }
                  missingCodeUuid
                  missingCodeDescription
                  missingCodeDescriptionUuid
                }
                dataPointReviewStatuses {
                  avroSize
                  isReviewed
                  dataPointReviewStatusUuid
                  reviewGroupUuid
                  dataPointUuid
                  dataPointId
                  operationType
                  operationTypeNumeric
                }
                markings {
                  avroSize
                  markingId
                  answerData
                  answerText
                  queryData
                  answerOnChange
                  closeOnChange
                  requiresResponse
                  acknowledged
                  tokenData
                  answerHash
                  markingUuid
                  markingTypeId
                  dataPointUuid
                  markingGroupUuid
                  acknowledgeUserUuid
                  resolveUserUuid
                  answerUserUuid
                  userUuid
                  stringUuid
                  operationType
                  text
                  markingActive
                  checkHash
                  created
                  updated
                  queryStatusId
                  answered
                  resolved
                  operationTypeNumeric
                }
                clinicalSignificances {
                  avroSize
                  comment
                  deleted
                  created
                  updated
                  clinicalSignificanceUuid
                  clinicalSignificanceCodeUuid
                  dataPointUuid
                  dataPointId
                  operationType
                  operationTypeNumeric
                }
                coderDecisions {
                  avroSize
                  coderDecisionId
                  codingDictionaryVersion
                  created
                  updated
                  deleted
                  coderDecisionUuid
                  dataPointUuid
                  operationType
                  operationTypeNumeric
                }
                coderValues {
                  avroSize
                  coderValueId
                  value
                  term
                  created
                  updated
                  deleted
                  coderValueUuid
                  coderDecisionUuid
                  coderColumnUuid
                  codingColumnName
                  operationType
                  operationTypeNumeric
                }
                studies {
                  avroSize
                  studyUuid
                  studyName
                  studyId
                  studyNameUuid
                  sourceSystemId
                }
                studyEnvironments {
                  avroSize
                  studyEnvironmentId
                  studyEnvironmentUuid
                  studyUuid
                  environment
                  environmentUuid
                }
                studyEnvironmentSites {
                    avroSize
                    studyEnvironmentSiteId
                    studyEnvironmentSiteNumber
                    studyEnvironmentSiteUuid
                    studyEnvironmentUuid
                    siteUuid
                }
                metadata {
                  _mdsol_lineage {
                    key
                    value
                  }
                  _mdsol_timeliness {
                    key
                    value
                  }
                  _mdsol_updated_at
                  messageId
                  raveDatabaseVersion
                  raveUrl
                  studyEnvironment
                  studyEnvironmentUuid
                  studyGroupUuid
                  studyUuid
                  transactionEndTime
                  transactionId
                  eventType
                  roleName
                  roleUuid
                  targetObjectType
                  targetObjectUuid
                  traceparent
                  userLogin
                  userUuid
                  messageTime
                }
              }
              ... on ArchitectMessageBody {
                crfVersions {
                    avroSize
                    crfVersionUuid
                    crfVersionId
                    edcExternalUuid
                    edcExternalVersionNumber
                    ecoaExternalUuid
                    ecoaExternalVersionNumber
                    crfVersionNameUuid
                    crfVersionName
                    defaultMatrixDefinitionUuid
                    primaryFormDefinitionUuid
                    created
                    updated
                    labStandardGroupUuid
                    referenceLabUuid
                    alertLabUuid
                    isValid
                    isDraft
                    hasClinicalData
                    isLocked
                    studyUuid
                    operationType
                    operationTypeNumeric
                }
                crfFieldReviewGroups {
                    avroSize
                    crfFieldReviewGroupUuid
                    crfVersionUuid
                    crfFieldUuid
                  fieldId
                    reviewGroupUuid
                  reviewGroupId
                    operationType
                    operationTypeNumeric
                }
                crfDerivationSteps {
                    avroSize
                    crfDerivationStepId
                    created
                    crfCustomFunctionUuid
                    crfDerivationStepUuid
                    crfDerivationUuid
                    crfFieldUuid
                    crfFolderUuid
                    crfVariableUuid
                    crfVersionUuid
                    customFunctionDefinitionUuid
                    dataFormat
                    derivationDefinitionUuid
                    fieldDefinitionUuid
                    folderDefinitionUuid
                    instanceRepeatNumber
                    logicalRecordPosition
                    pageRepeatNumber
                    recordPosition
                    stepFunction
                    stepOrdinal
                    stepValue
                    updated
                    variableDefinitionUuid
                    operationType
                    operationTypeNumeric
                }
                crfDerivations {
                    avroSize
                    crfDerivationId
                    crfDerivationUuid
                    crfVersionUuid
                    derivationVersionUuid
                    derivationDefinitionUuid
                    derivationOid
                    fieldDefinitionUuid
                    crfFieldUuid
                    folderDefinitionUuid
                    crfFolderUuid
                    active
                    deleted
                    hasOrderLRP
                    hasMaxMinLRP
                    created
                    updated
                    operationType
                    operationTypeNumeric
                }
                derivationDefinitions {
                    avroSize
                    derivationDefinitionUuid
                    studyUuid
                    derivationOid
                    created
                    operationType
                    operationTypeNumeric
                }
                derivationVersions {
                    avroSize
                    derivationVersionUuid
                    derivationDefinitionUuid
                    derivationOid
                    derivationName
                    recordPosition
                    allVariablesInFolders
                    allVariablesInFields
                    pageRepeatNumber
                    instanceRepeatNumber
                    isNotBatchRunnable
                    isNotUploadRunnable
                    fieldDefinitionUuid
                    folderDefinitionUuid
                    created
                    operationType
                    operationTypeNumeric
                }
                crfFolders {
                    avroSize
                    crfFolderId
                    crfFolderUuid
                    crfFolderExternalUuid
                    ordinal
                    created
                    updated
                    crfVersionUuid
                    folderVersionUuid
                    folderDefinitionUuid
                    operationType
                    operationTypeNumeric
                }
                folderDefinitions {
                    avroSize
                    folderDefinitionUuid
                    folderOid
                    studyUuid
                    created
                    operationType
                    operationTypeNumeric
                }
                folderVersions {
                    avroSize
                    folderVersionUuid
                    folderDefinitionUuid
                    folderNameUuid
                    folderName
                    accessDays
                    startWinDays
                    targetDays
                    endWinDays
                    overdueDays
                    closeDays
                    isReusable
                    parentFolderDefinitionUuid
                    created
                    operationType
                    operationTypeNumeric
                }
                crfFolderForms {
                  avroSize,
                  crfFolderFormId
                  crfFolderFormUuid
                  crfVersionUuid
                  crfMatrixUuid
                  crfFolderUuid
                  crfFolderFormExternalUuid
                  created
                  updated
                  operationType
                  crfFormUuid
                  operationTypeNumeric
                }
                crfFormRestrictions {
                  avroSize,
                  crfFormRestrictionId
                  crfFormRestrictionUuid
                  crfVersionUuid
                  crfFormUuid
                  roleUuid
                  restrictionType
                  created
                  updated
                  operationType
                  operationTypeNumeric
                }
                crfForms {
                    avroSize
                    crfFormId
                    crfFormUuid
                    crfFormExternalUuid
                    crfFormExternalVersionNumber
                    ordinal
                    formActive
                    created
                    updated
                    crfVersionUuid
                    formVersionUuid
                    formDefinitionUuid
                    operationType
                    operationTypeNumeric
                }
                formDefinitions {
                    avroSize
                    formDefinitionUuid
                    formOid
                    studyUuid
                    created
                    operationType
                    operationTypeNumeric
                }
                formVersions {
                    avroSize
                    formVersionUuid
                    formDefinitionUuid
                    formNameUuid
                    formName
                    helpTextUuid
                    helpText
                    startLogs
                    maxLogs
                    allowLogAdd
                    allowLogInactivate
                    editDirection
                    isTemplate
                    confirmationStyle
                    linkFormOID
                    linkFolderOID
                    isSignatureRequired
                    ddeOption
                    isEproForm
                    isQuestionnaire
                    created
                    operationType
                    operationTypeNumeric
                }
                crfFields {
                    avroSize
                    crfFieldId
                    crfFieldUuid
                    crfFieldExternalUuid
                    crfFormUuid
                    formDefinitionUuid
                    variableDefinitionUuid
                    crfVariableUuid
                    ordinal
                    columnOrdinal
                    fieldActive
                    created
                    updated
                    crfVersionUuid
                    fieldVersionUuid
                    fieldDefinitionUuid
                    operationType
                    operationTypeNumeric
                }
                fieldDefinitions {
                    avroSize
                    fieldDefinitionUuid
                    formDefinitionUuid
                    fieldOid
                    studyUuid
                    created
                    operationType
                    operationTypeNumeric
                }
                fieldVersions {
                    avroSize
                    fieldVersionUuid
                    fieldDefinitionUuid
                    formDefinitionUuid
                    fieldNameUuid
                    fieldName
                    indentLevel
                    fieldNumberId
                    preTextUuid
                    preText
                    postTextUuid
                    postText
                    headerTextUuid
                    headerText
                    helpTextUuid
                    helpText
                    sourceDocument
                    isLog
                    defaultValue
                    controlType
                    isChild
                    requiresTranslation
                    otherVisits
                    isVisible
                    canSetRecordDate
                    canSetDataPageDate
                    canSetInstanceDate
                    canSetSubjectDate
                    analyteUuid
                    isClinicalSignificance
                    doesNotBreakSignature
                    isRequired
                    isFormatMatched
                    isFutureDateTime
                    lowerRange
                    upperRange
                    ncLowerRange
                    ncUpperRange
                    isVisualVerify
                    sasLabel
                    sasFormat
                    draftDomainId
                    acceptableFileExtensions
                    eproFormat
                    conceptUri
                    attributeUri
                    componentUuid
                    componentAppOid
                    componentName
                    created
                    operationType
                    operationTypeNumeric
                }
                crfFieldRestrictions {
                    avroSize,
                    crfFieldRestrictionId
                    crfFieldRestrictionUuid
                    crfFieldUuid
                    crfVersionUuid
                    restrictionType
                    roleUuid
                    created
                    updated
                    operationType
                    operationTypeNumeric
                }
                crfMatrices {
                    avroSize
                    crfMatrixId
                    crfMatrixUuid
                    crfMatrixExternalUuid
                    crfMatrixExternalVersionNumber
                    created
                    updated
                    crfVersionUuid
                    matrixVersionUuid
                    matrixDefinitionUuid
                    operationType
                    operationTypeNumeric
                }
                matrixDefinitions {
                    avroSize
                    matrixDefinitionUuid
                    matrixOid
                    studyUuid
                    created
                    operationType
                    operationTypeNumeric
                }
                matrixVersions {
                    avroSize
                    matrixVersionUuid
                    matrixDefinitionUuid
                    matrixNameUuid
                    matrixName
                    addable
                    maximum
                    created
                    operationType
                    operationTypeNumeric
                }
                crfDataDictionaries {
                    avroSize
                    crfDataDictionaryId
                    crfDataDictionaryUuid
                    crfVersionUuid
                    dataDictionaryDefinitionUuid
                    dataDictionaryVersionUuid
                    externalUuid
                    externalVersionNumber
                    created
                    updated
                    operationType
                    operationTypeNumeric
                }
                dataDictionaryDefinitions {
                    avroSize
                    dataDictionaryDefinitionUuid
                    dataDictionaryOid
                    studyUuid
                    created
                    operationType
                    operationTypeNumeric
                }
                dataDictionaryVersions {
                    avroSize
                    dataDictionaryDefinitionUuid
                    dataDictionaryName
                    dataDictionaryNameUuid
                    dataDictionaryOid
                    dataDictionaryVersionUuid
                    hasOther
                    nameSpaceId
                    created
                    operationType
                    operationTypeNumeric
                }
                crfDataDictionaryEntries {
                    avroSize
                    crfDataDictionaryEntryId
                    crfDataDictionaryEntryUuid
                    crfDataDictionaryUuid
                    crfVersionUuid
                    dataDictionaryDefinitionUuid
                    dataDictionaryEntryDefinitionUuid
                    dataDictionaryEntryVersionUuid
                    externalUuid
                    ordinal
                    created
                    updated
                    operationType
                    operationTypeNumeric
                }
                dataDictionaryEntryDefinitions {
                    avroSize
                    codedData
                    dataDictionaryDefinitionUuid
                    dataDictionaryEntryDefinitionUuid
                    created
                    operationType
                    operationTypeNumeric
                }
                dataDictionaryEntryVersions {
                    avroSize
                    codedData
                    dataDictionaryDefinitionUuid
                    dataDictionaryEntryDefinitionUuid
                    dataDictionaryEntryOid
                    dataDictionaryEntryVersionUuid
                    specify
                    userDataString
                    userDataStringUuid
                    created
                    operationType
                    operationTypeNumeric
                }
                crfUnitDictionaries {
                    avroSize
                    crfUnitDictionaryId
                    crfUnitDictionaryUuid
                    crfVersionUuid
                    externalVersionNumber
                    externalUuid
                    unitDictionaryOid
                    created
                    updated
                    standardCrfUnitDictionaryEntryId
                    standardUnitDictionaryEntryDefinitionUuid
                    unitDictionaryVersionUuid
                    unitDictionaryDefinitionUuid
                    operationType
                    operationTypeNumeric
                }
                unitDictionaryDefinitions {
                    avroSize
                    unitDictionaryDefinitionUuid
                    unitDictionaryOid
                    studyUuid
                    created
                    operationType
                    operationTypeNumeric
                }
                unitDictionaryVersions {
                    avroSize
                    unitDictionaryVersionUuid
                    unitDictionaryDefinitionUuid
                    unitDictionaryOid
                    unitDictionaryNameUuid
                    unitDictionaryName
                    nameSpaceId
                    standardUnitDictionaryEntryDefinitionUuid
                    created
                    operationType
                    operationTypeNumeric
                }
                crfUnitDictionaryEntries {
                    avroSize
                    crfUnitDictionaryEntryId
                    crfUnitDictionaryEntryUuid
                    crfVersionUuid
                    unitDictionaryEntryVersionUuid
                    unitDictionaryEntryDefinitionUuid
                    unitDictionaryDefinitionUuid
                    crfUnitDictionaryUuid
                    ordinal
                    externalUuid
                    created
                    updated
                    operationType
                    operationTypeNumeric
                }
                unitDictionaryEntryDefinitions {
                    avroSize
                    unitDictionaryEntryDefinitionUuid
                    unitDictionaryDefinitionUuid
                    codedUnit
                    created
                    operationType
                    operationTypeNumeric
                }
                unitDictionaryEntryVersions {
                    avroSize
                    unitDictionaryEntryVersionUuid
                    unitDictionaryDefinitionUuid
                    unitDictionaryEntryDefinitionUuid
                    codedUnit
                    unitDictionaryEntryNameUuid
                    unitDictionaryEntryName
                    constantA
                    constantB
                    constantC
                    constantK
                    userUnitStringUuid
                    userUnitString
                    unitDictionaryEntryOid
                    created
                    operationType
                    operationTypeNumeric
                }
                crfVariables {
                    avroSize
                    crfVariableId
                    crfVariableUuid
                    crfVersionUuid
                    variableVersionUuid
                    variableDefinitionUuid
                    dataDictionaryDefinitionUuid
                    crfDataDictionaryUuid
                    unitDictionaryDefinitionUuid
                    crfUnitDictionaryUuid
                    derivationDefinitionUuid
                    crfDerivationUuid
                    isUsingCoder
                    codingDictionaryId
                    deleted
                    created
                    updated
                    operationType
                    operationTypeNumeric
                }
                variableDefinitions {
                    avroSize
                    variableDefinitionUuid
                    studyUuid
                    variableOid
                    created
                    operationType
                    operationTypeNumeric
                }
                variableVersions {
                    avroSize
                    variableVersionUuid
                    variableDefinitionUuid
                    variableName
                    dataFormat
                    nameSpaceID
                    dataDictionaryDefinitionUuid
                    unitDictionaryDefinitionUuid
                    derivationDefinitionUuid
                    created
                    operationType
                    operationTypeNumeric
                }
                studies {
                    avroSize
                    studyId
                    studyUuid
                    studyName
                    studyNameUuid
                    sourceSystemId
                }
                metadata {
                    _mdsol_lineage {
                      key
                      value
                    }
                    _mdsol_timeliness {
                      key
                      value
                    }
                    _mdsol_updated_at
                    messageId
                    raveDatabaseVersion
                    raveUrl
                    studyGroupUuid
                    studyUuid
                    transactionEndTime
                    transactionId
                    eventType
                    roleName
                    roleUuid
                    targetObjectType
                    targetObjectUuid
                    traceparent
                    userLogin
                    userUuid
                    messageTime
                }
              }
              ... on ConfigurationMessageBody {
                roles {
                    avroSize
                    roleUuid
                    rolePermissions
                    active
                    created
                    updated
                    viewAllSites
                    createSubjects
                    modifyPrimaryRecord
                    isInvestigator
                    subjectAdmin
                    seeAudits
                    changeInvestigator
                    shareSubject
                    canViewStudyGrid
                    canBatchSign
                    canInactivateForm
                    canReactivateForm
                    usePrimarySubjectName
                    canUseTemplates
                    canModifyTemplates
                    setClinicalSignificance
                    useLabMaintenance
                    viewAllSubjects
                    pass1DataEntry
                    pass2DataEntry
                    reconcile
                    roleNameUuid
                    roleName
                    canUseDCFs
                    canUseAdvancedDCFs
                    moduleId
                    moduleName
                    operationType
                    operationTypeNumeric
                 }
                 roleActions {
                    avroSize
                    roleActionUuid
                    roleUuid
                    actionTypeId
                    actionTypeName
                    restrictionMask
                    restrictionStatus
                    markingGroupUuid
                    reviewGroupUuid
                    created
                    updated
                    operationType
                    operationTypeNumeric
                 }
                 markingGroups {
                    avroSize
                    markingGroupUuid
                    markingGroupActive
                    created
                    updated
                    markingGroupNameUuid
                    markingGroupName
                    markingGroupCategoryId
                    markingGroupCategoryName
                    operationType
                    operationTypeNumeric
                 }
                 reviewGroups {
                    avroSize
                    reviewGroupUuid
                    reviewGroupActive
                    created
                    updated
                    reviewGroupNameUuid
                    reviewGroupName
                    operationType
                    operationTypeNumeric
                 }
                 localizations {
                    avroSize
                    localizationUuid
                    locale
                    helpFolder
                    nameFormat
                    numberFormat
                    dateFormat
                    submitOnEnter
                    culture
                    descriptionUuid
                    dateTimeFormat
                    description
                    created
                    updated
                    operationType
                    operationTypeNumeric
                 }
                 protocolDeviationClasses {
                   active
                   avroSize
                   created
                   deleted
                   operationType
                   protocolDeviationClassUuid
                   classValue
                   classValueUuid
                   updated
                   operationTypeNumeric
                 }
                 protocolDeviationCodes {
                   active
                   avroSize
                   created
                   deleted
                   operationType
                   protocolDeviationCodeUuid
                   codeValue
                   codeValueUuid
                   updated
                   operationTypeNumeric
                 }
                 roleSubjectStatusAccesses {
                    avroSize
                    roleSubjectStatusAccessUuid
                    roleUuid
                    subjectStatusUuid
                    operationType
                    operationTypeNumeric
                 }
                 subjectStatuses {
                    avroSize
                    subjectStatusUuid
                    stringNameUuid
                    stringName
                    subjectStatusCategoryId
                    subjectStatusCategoryName
                    deleted
                    isDefault
                    created
                    updated
                    operationType
                    operationTypeNumeric
                 }
                 configurations {
                    avroSize
                    configKey
                    configValue
                    created
                    updated
                    operationType
                    operationTypeNumeric
                 }
                 metadata {
                  ...metadataFields
                 }
              }
              ... on EnumMessageBody {
                actionTypes {
                  avroSize
                  operationType
                  operationTypeNumeric
                  value
                  text
                }
                auditCategories {
                  avroSize
                  operationType
                  operationTypeNumeric
                  value
                  text
                }
                auditSubCategories {
                  avroSize
                  auditSubCategoryId
                  auditSubCategoryName
                  auditCategoryId
                  auditCategoryName
                  operationType
                  operationTypeNumeric
                }
                impliedActionTypes {
                  avroSize
                  actionTypeId
                  actionTypeName
                  operationType
                  operationTypeNumeric
                  impliedActionTypeId
                  impliedActionTypeName
                }
                markingGroupCategories {
                  avroSize
                  operationType
                  operationTypeNumeric
                  value
                  text
                }
                metadata {
                  ...metadataFields
                }
                modules {
                  avroSize
                  operationType
                  operationTypeNumeric
                  value
                  text
                }
                objectTypes {
                  avroSize
                  operationType
                  operationTypeNumeric
                  value
                  text
                }
                subjectStatusCategories {
                  avroSize
                  operationType
                  operationTypeNumeric
                  value
                  text
                }
                queryStatuses {
                  avroSize
                  operationType
                  operationTypeNumeric
                  value
                  text
                }
              }
              ... on AuditMessageBody {
                audits {
                  auditSubCategoryId
                  auditTime
                  auditUserUuid
                  auditUuid
                  avroSize
                  databaseTime
                  internalUserUuid
                  objectTypeId
                  objectUuid
                  property
                  readable
                  value
                }
                metadata {
                  ...metadataFields
                }
              }
              ... on CoderConfigMessageBody {
                codingColumns {
                    avroSize
                    codeMaxLength
                    codeSASSuffix
                    codeSuffix
                    codingColumnName
                    codingColumnUuid
                    codingDictionaryUuid
                    created
                    defaultCodingLevel
                    operationType
                    termMaxLength
                    termSASSuffix
                    termSuffix
                    updated
                    operationTypeNumeric
                }
                 codingDictionaries {
                    avroSize
                    codingDictionaryUuid
                    codingDomainName
                    codingDomainUuid
                    created
                    dictionaryName
                    dictionaryVersion
                    isCoderDict
                    operationType
                    updated
                    operationTypeNumeric
                }
                metadata {
                  ...metadataFields
                }
              }
              ... on PlatformObjectMessageBody {
                clientDivisionOrganizations{
                  addressLine1
                  addressLine2
                  addressLine3
                  avroSize
                  city
                  clientDivisionOrganizationId
                  clientDivisionOrganizationUuid
                  country
                  created
                  eTag
                  fax
                  lastExternalUpdateDate
                  oid
                  operationType
                  operationTypeNumeric
                  postalCode
                  siteActive
                  siteGroupId
                  siteName
                  siteNameUuid
                  siteNumber
                  state
                  telephone
                  updated
                }
                studies {
                  abbreviation
                  abbreviationStringUuid
                  avroSize
                  created
                  createdByUserUuid
                  customerApiCompatibilityVersion
                  description
                  descriptionStringUuid
                  enableVerifyReviewOnInactive
                  isAllCopySources
                  isClassic
                  isLocked
                  isRcmEnabled
                  legacyCustomerApiCompatibilityVersion
                  operationType
                  operationTypeNumeric
                  pageRedirection
                  studyActive
                  studyId
                  studyName
                  studyNameUuid
                  studyTypeId
                  studyUuid
                  updated
                }
                studyEnvironments {
                  allowLockedDataPointChange
                  avroSize
                  created
                  defaultCrfVersionUuid
                  deleted
                  enrollmentTarget
                  environment
                  environmentStringUuid
                  isUserDeactivated
                  lastExternalUpdateDate
                  oid
                  operationType
                  operationTypeNumeric
                  preventExecuteChecksOnCoderDictionaryVersionUpdate
                  startDate
                  studyEnvironmentActive
                  studyEnvironmentId
                  studyEnvironmentSubjectCount
                  studyEnvironmentUuid
                  studyUuid
                  testStudyEnvironment
                  updated
                }
                studyEnvironmentSites {
                  allowUnitsOnly
                  avroSize
                  clientDivisionOrganizationUuid
                  created
                  crfVersionUuid
                  defaultLabType
                  deleted
                  enrollmentCap
                  enrollmentTarget
                  isUserDeactivated
                  lastExternalUpdateDate
                  operationType
                  operationTypeNumeric
                  studyEnvironmentSiteId
                  studyEnvironmentSiteUuid
                  studyEnvironmentUuid
                  studySiteActive
                  studySiteNumber
                  studySiteSubjectCount
                  updated
                }
                metadata {
                  ...metadataFields
                }
              }
              ... on RaveUserMessageBody {
                externalUsers {
                  avroSize
                  email
                  externalId
                  externalUserId
                  externalUserUuid
                  lastExternalUpdateDate
                  locale
                  login
                  name
                  operationType
                  operationTypeNumeric
                }
                internalUsers {
                  avroSize
                  created
                  email
                  enabled
                  externalUserId
                  internalUserUuid
                  localization
                  login
                  operationType
                  operationTypeNumeric
                  updated
                  userActive
                  userId
                }
                metadata {
                  ...metadataFields
                }
              }
              ... on DataVerificationHashMessageBody {
                dataVerificationHashes {
                  avroSize
                  bucketId
                  objectName
                  batchSize
                  includedRowCount
                  orderColumn
                  lastUpdated
                  hashValue
                  appVersion
                  operationType
                  operationTypeNumeric
                }
                metadata {
                  ...metadataFields
            	  }
              }
              ... on InterimLockMessageBody{
                metadata {
                  studyUuid
                  requestUuid
                  patientCount
                  dataPageCount
                  recordCount
                  raveUrl
                }
                total
                completed
                partiallyCompleted
                failed
                preconditionFailed
                taskNumber
                skipped

                dataPages {
                  avroSize
                  studyEnvironmentSiteUuid
                  patientUuid
                  patientName
                  patientSecondaryName
                  dataPageUuid
                  instanceUuid
                  formOid
                  formUuid
                  formNameUuid
                  folderOid
                  folderUuid
                  folderNameUuid
                  records {
                    avroSize
                    recordUuid
                    recordPosition
                    preconditionFailed
                    failed
                    processed
                    status
                    totalDataPointCount
                    modifiedDataPointCount
                    errorMessage
                  }

                  locales {
                    dataPageName
                    instanceName
                    siteName
                    default
                  }

                }
              }
            }
          }
        }
        ... on EventMessagingDataLoadTask {
          eventMessageType {
            isUnknown
            isShared
            isEdcDataLoad
            isEDC
            isConfiguration
            isArchitect
            isArchitectDataLoad
            isAudits
            isAuditDataLoad
            isApplication
            isEnum
            isEnumDataLoad
            isCoderConfig
            isCoderConfigDataLoad
            isCrfVersion
            isCrfVersionDataLoad
            isObjectTag
            isObjectTagDataLoad
            isLocalizedDataString
            isLocalizedDataStringDataLoad
            isLocalizedString
            isLocalizedStringDataLoad
            isConfigurationDataLoad
            isDataVerificationHash
            isDataVerificationHashDataLoad
            isPlatformObjectDataLoad
            isRaveUserDataLoad
            isInterimLock
            isSyntheticData
          }
          objectId,
          changesAfterDate
        }
        ... on DataVerificationHashJobCreatorTask {
          startLsn
          endLsn
        }
        ... on DataVerificationHashTask {
          buckets
          dataVerificationHashObject
          createJsonFile
        }
        ... on DataVerificationHashRecalculationTask {
          batchSize
          buckets
          dataVerificationHashObject
          fromIdInclusive
          toIdExclusive
        }
        ... on InterimLockTask {
          subjectId
          recordIds
          taskNumber
        }
        ... on ChangeDataDataLoadTask {
          changeDataDataLoadObjects {
            objectTypeId
            objectIds
          }
          taskData {
            subjectId
            dataPointIds
          }
        }
      }
      ... on BulkOperationJob {
        studyId
        studyUuid
        requestId
        requestSource
      }

      ... on StatusRollupJob {
        studyId
      }

      ... on InterimLockJob {
        studyId
        roleId
        userId
        totalPatients
        totalDataPages
        totalRecords
        input {
          action
          studyEnvironmentSiteUuids
          visitExcludedFormOids
          visitIncludedFolderOids
          patientFilter {
            data
            fieldOid
            folderOid
            operation
          }
          logLines {
            formOid
            folderOid
            action
            filter {
              operation
              fieldOid
              data
            }
          }
        }
      }
      ... on EventMessagingDataLoadJob {
        changesAfterDate
      }
      ... on EventMessagingSendJob {
        messageType {
          isApplication
          isArchitect
          isArchitectDataLoad
          isConfiguration
          isAudits
          isAuditDataLoad
          isDataVerificationHash
          isDataVerificationHashDataLoad
          isEDC
          isEdcDataLoad
          isShared
          isUnknown
          isEnum
          isEnumDataLoad
          isCoderConfig
          isCoderConfigDataLoad
          isPlatformObject
          isRaveUser
          isCrfVersion
          isCrfVersionDataLoad
          isObjectTag
          isObjectTagDataLoad
          isLocalizedDataString
          isLocalizedDataStringDataLoad
          isLocalizedString
          isLocalizedStringDataLoad
          isConfigurationDataLoad
          isPlatformObjectDataLoad
          isRaveUserDataLoad
          isSyntheticData
          isInterimLock
        }
      }
      ... on TaskSummaryJob {
        studyId
      }
      ... on TaskSummaryValidationJob {
        studyUuid
        studyEnvironmentSiteUuid
        performSiteLevelValidation
        siteLevelValidationResult
        siteValidationItems {
          roleId
          parentCount {
            answeredQuery
            answeredQueryReadonly
            answeredQueryCount
            answeredQueryReadonlyCount
            cancelQuery
            cancelQueryCount
            coder
            dataLock
            dataLockEnqueued
            entryLock
            entryLockEnqueued
            nonConformant
            openQuery
            openQueryReadonly
            openQueryCount
            openQueryReadonlyCount
            overdue
            review
            reviewed
            reviewReadonly
            reviewEnqueued
            reviewRequired
            signature
            signatureEnqueued
            sticky
            stickyReadonly
            submitted
            partiallySubmitted
            notSubmitted
            signed
            signatureNotCurrent
            signatureRequired
            complete
            incomplete
            notLocked
            notFrozen
            locked
            frozen
            notEmpty
            total
            inactive
          }
          childrenCount {
            answeredQuery
            answeredQueryReadonly
            answeredQueryCount
            answeredQueryReadonlyCount
            cancelQuery
            cancelQueryCount
            coder
            dataLock
            dataLockEnqueued
            entryLock
            entryLockEnqueued
            nonConformant
            openQuery
            openQueryReadonly
            openQueryCount
            openQueryReadonlyCount
            overdue
            review
            reviewed
            reviewReadonly
            reviewEnqueued
            reviewRequired
            signature
            signatureEnqueued
            sticky
            stickyReadonly
            submitted
            partiallySubmitted
            notSubmitted
            signed
            signatureNotCurrent
            signatureRequired
            complete
            incomplete
            notLocked
            notFrozen
            locked
            frozen
            notEmpty
            total
            inactive
          }
        }
      }
      ... on TaskSummaryRecalculationJob {
        studyUuid
        studyEnvironmentSiteUuid
        performSiteLevelRecalculation
        siteLevelRecalculationSuccessful
      }

      ... on SyntheticDataJob {
        studyUuid
        environmentUuid
        siteUuid
        crfVersionUuid
        patientCount
      }
    }
  `,
}

const GET_JOBS: TypedDocumentNode<Query> = gql`
  query GetJobs(
    $first: Int,
    $after: String,
    $last: Int,
    $before: String,
    $order: [LegionJobSortInput!],
    $where: LegionJobFilterInput,
  ) {
    jobs(
      first: $first,
      after: $after,
      last: $last,
      before: $before,
      order: $order,
      where: $where,
    ) {
      ...jobsData
    }
  }

  ${fragments.jobFields}
  ${fragments.jobsData}
`

const GET_JOB_DETAILS: TypedDocumentNode<Query> = gql`
  query GetJobDetails(
    $jobId: UUID
  ) {
    jobs (
      where: {jobId: {eq: $jobId}}
    ) {
      nodes {...jobDetailFields}
    }
  }

  ${fragments.jobDetailsFields}
`
const GET_JOB_TYPE_ACTION_OPTIONS = gql`
  query jobTypeActionOptions {
    jobTypeActionOptions {
      actionType
      icon
      configuration {
        jobType
        jobStatus
      }
    }
}`

export const DOWNLOAD_JOB_REPORT = gql`
  query DownloadJobReport($jobId: UUID!) {
    downloadJobReport(jobId: $jobId) {
      content
      type
    }
  }
`

export const getJobs = (
  pagination: GraphQLPaginationType,
  order: JobOrderType,
  where: LegionJobFilterInput
) => {
  const { first, after, last, before } = pagination

  return client.query({
    query: GET_JOBS,
    variables: {
      first,
      after,
      last,
      before,
      order,
      where,
    },
  })
}

export const getJobDetails = (jobId: string) => client.query({
  query: GET_JOB_DETAILS,
  variables: {
    jobId,
  },
})

export const getJobTypeActionOptions = () =>
  client.query({
    query: GET_JOB_TYPE_ACTION_OPTIONS,
  })

export const getJobReport = (jobId: string) =>
  client.query({
    query: DOWNLOAD_JOB_REPORT,
    variables: {
      jobId,
    },
  })